/**
 * Selectors for the lexicons module (VE-24948 / U5).
 *
 * Memoized with `createSelector` per the repo pattern: selectors transform,
 * actions drive events, sagas own side effects.
 */

import { createSelector } from 'reselect';

import { lexiconEntryDuplicateKey } from './csvValidator';
import { LEXICON_MAX_ENTRIES } from './limits';
import {
  LexiconEntry,
  LexiconEntryInput,
  LexiconSummary,
  LexiconsState,
  isDraftEntryId,
} from './models';
import { namespace } from './store';

export const selectStore = (state: { [namespace]: LexiconsState }) =>
  state[namespace];

export const selectIsFetching = createSelector(
  selectStore,
  (s) => s.isFetching
);

export const selectLexiconsById = createSelector(
  selectStore,
  (s) => s.lexicons
);

export const selectDeletedLexiconsById = createSelector(
  selectStore,
  (s) => s.deletedLexicons
);

/** True while the DELETED list is in flight — see `LexiconsState`. */
export const selectIsFetchingDeletedLexicons = createSelector(
  selectStore,
  (s) => s.isFetchingDeleted
);
export const selectIsDrawerOpen = createSelector(
  selectStore,
  (s) => s.isDrawerOpen
);
export const selectIsMutating = createSelector(
  selectStore,
  (s) => s.isMutating
);
export const selectMutationNotice = createSelector(
  selectStore,
  (s) => s.mutationNotice
);
export const selectEditing = createSelector(selectStore, (s) => s.editing);
export const selectCsvImport = createSelector(selectStore, (s) => s.csvImport);

/**
 * True when lexicons are not provisioned in this environment (NFR-U5-12).
 *
 * Consumers must treat this as "hide the feature", not "show an error": it is
 * the real state in an environment whose data registry has not been bootstrapped
 * yet, GOV before its provisioning request lands being the expected case.
 */
export const selectIsNotConfigured = createSelector(
  selectStore,
  (s) => s.isNotConfigured
);

function byName(
  byId: Readonly<Record<string, LexiconSummary>>
): readonly LexiconSummary[] {
  return Object.values(byId).sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  );
}

/**
 * THE LIBRARY: every live lexicon in the org, sorted by name.
 *
 * The whole list, not a page — which is what lets the settings table sort and
 * page over rows it already holds, so sorting by Entries orders the LIBRARY
 * rather than reordering whichever 25 rows a server page happened to send.
 *
 * It used to mean "whichever list the store happens to hold", because the store
 * held one page of one view and the settings page chose which. That is how the
 * editor came to be offered the trash (VE-30783). Deleted rows are
 * `selectDeletedLexicons` and are never in here.
 *
 * It includes DISABLED lexicons, deliberately: the settings page is where a
 * disabled lexicon gets re-enabled, so hiding it there would make it
 * unreachable. Only `selectApplicableLexicons` reads `enabled`, and only the
 * pickers use that.
 */
export const selectLexicons = createSelector(selectLexiconsById, byName);

/** THE TRASH: every soft-deleted lexicon in the org, sorted by name. */
export const selectDeletedLexicons = createSelector(
  selectDeletedLexiconsById,
  byName
);

/**
 * How many soft-deleted lexicons the org holds — the "(N)" on the link.
 *
 * The length of the list, now that the list is here. It used to be its own
 * piece of store state fed by `countDeletedLexicons`, a query that fetched 101
 * rows, counted them, discarded them and capped the answer at "100+"
 * (VE-29495) — a number that no longer has to be approximate.
 */
export const selectDeletedLexiconCount = createSelector(
  selectDeletedLexiconsById,
  (byId) => Object.keys(byId).length
);

/**
 * Lexicons that can actually be applied (BR-4).
 *
 * An empty lexicon is a legal intermediate state — you must create it before
 * you can fill it — but offering it for application would be a silent no-op
 * that reads to the user as a bug.
 *
 * THE ONLY PLACE `enabled` IS READ, and the reason both entry points honour it
 * without knowing it exists: the upload selector and the editor's apply panel
 * share this selector, so a lexicon switched off in settings disappears from
 * both or from neither. `!== false` rather than `=== true` — every record
 * written before the column existed omits the field, and reading absence as
 * "off" would switch an org's whole library off at once.
 *
 * `isDeleted !== true` is a BACKSTOP, not the mechanism. `selectLexicons` is
 * the live list — a filtered query, into a slot of its own — so a deleted row
 * cannot reach here in the first place. It stays because it costs nothing and
 * because the mechanism it backs up has failed once already: when one slot held
 * whichever view the settings page was on, this line was the only thing between
 * the trash and the editor's picker, and it was not enough (VE-30783).
 *
 * It does not touch lexicons ALREADY applied to a document: those come from the
 * session, and their detections carry snapshots (U5-FR2). Switching a lexicon
 * off stops it being chosen again; it does not retract what it found.
 */
export const selectApplicableLexicons = createSelector(
  selectLexicons,
  (lexicons) =>
    lexicons.filter(
      (l) => l.entryCount > 0 && l.enabled !== false && l.isDeleted !== true
    )
);

/** Duplicate keys of the entries in the lexicon being edited (BR-23 context). */
export const selectEditingExistingKeys = createSelector(
  selectEditing,
  (editing): ReadonlySet<string> =>
    new Set((editing?.draft.entries ?? []).map(lexiconEntryDuplicateKey))
);

/**
 * What Save has to do (VE-24948 / U5).
 *
 * The draft is the FULL desired list; this diffs it against the persisted
 * lexicon. `added` are the `draft:`-id entries (stripped of their temporary
 * id — the service assigns real ones, BR-8); `removedEntryIds` are persisted
 * ids no longer in the draft; `nameChanged` compares trimmed names.
 */
export interface LexiconDraftDiff {
  readonly isCreate: boolean;
  readonly name: string;
  readonly nameChanged: boolean;
  readonly added: readonly LexiconEntryInput[];
  /**
   * Existing entries whose CONTENT changed, keeping their id and position.
   *
   * A third bucket, and it had to be one. While a draft could only add or
   * remove, `added` and `removedEntryIds` covered every change an entry could
   * undergo. An edit does neither: the entry keeps its real id, so it is not in
   * `added` (that filters on `isDraftEntryId`) and not in `removedEntryIds`
   * (its id is still in the draft). It fell through both and the save discarded
   * it with no error — which is why editing was not merely missing from the UI
   * but unrepresentable.
   */
  readonly updated: readonly LexiconEntry[];
  /**
   * Live entries the draft has tombstoned — a SOFT delete.
   *
   * Separate from `updated` even though it is carried by the same field flip,
   * because the audit trail must say "removed" rather than "changed": the two
   * are very different things to find in a disclosure record.
   */
  readonly deletedEntryIds: readonly string[];
  /**
   * Entries gone from the draft array entirely — a HARD delete.
   *
   * Only Purge produces this, and only behind a confirmation. It is what
   * reclaims room under the schema's `maxItems: 5000`, which counts tombstones
   * like any other item.
   */
  readonly purgedEntryIds: readonly string[];
  readonly isDirty: boolean;
}

/**
 * True when two entries differ in anything that is persisted.
 *
 * Compared field by field rather than by a deep-equality helper so the set is
 * explicit: adding a persisted field to `LexiconEntry` and forgetting it here
 * would make edits to that field invisible to the diff — the same silent
 * discard this bucket exists to end. (`kind` was on this list while Keyword and
 * Phrase were separate. They are one kind now and nothing stores it, so there
 * is no relabelling left to lose.)
 */
const entryContentDiffers = (a: LexiconEntry, b: LexiconEntry): boolean =>
  a.term !== b.term ||
  a.type !== b.type ||
  (a.caseSensitive ?? false) !== (b.caseSensitive ?? false) ||
  (a.wholeWord ?? false) !== (b.wholeWord ?? false) ||
  a.redactionCodeId !== b.redactionCodeId ||
  (a.isDeleted ?? false) !== (b.isDeleted ?? false);

export const selectDraftDiff = createSelector(
  selectEditing,
  selectLexiconsById,
  (editing, byId): LexiconDraftDiff | null => {
    if (!editing) {
      return null;
    }
    // Diff against the OPEN-TIME baseline, and ONLY that. The list moves while
    // the drawer is open (every mutation refetches it), and a moved baseline
    // turned another admin's concurrent additions into this save's removals
    // (review-gate finding 3).
    //
    // There is no longer a fallback to the live record: the list holds
    // summaries, which have no entries, so there is nothing to fall back TO.
    // `baseline` is written by the drawer-open hydration and by the conflict
    // Reload, which are the only two ways a draft comes to exist.
    const live = editing.lexiconId ? byId[editing.lexiconId] : undefined;
    const baseName = editing.baseline?.name ?? live?.name;
    const baseEntries = editing.baseline?.entries ?? [];
    const baseEntryIds = baseEntries.map((e) => e.entryId);
    const baseById = new Map(baseEntries.map((e) => [e.entryId, e]));
    const name = editing.draft.name.trim();
    const draftIds = new Set(editing.draft.entries.map((e) => e.entryId));
    const added = editing.draft.entries
      .filter((e) => isDraftEntryId(e.entryId))
      .map(({ entryId: _draftId, ...input }) => input);
    // Gone from the array altogether: only Purge does this.
    const purgedEntryIds = baseEntryIds.filter((id) => !draftIds.has(id));
    // Only entries the baseline still knows about: a draft entry has no
    // baseline to differ from, and one the baseline has lost is a purge.
    const changed = editing.draft.entries.filter((entry) => {
      const base = baseById.get(entry.entryId);
      return base !== undefined && entryContentDiffers(entry, base);
    });
    // A tombstone going UP is a removal and is audited as one. Everything else
    // — including a tombstone coming back down, which is a restore — is an
    // ordinary content change.
    const wasJustDeleted = (entry: LexiconEntry): boolean =>
      !!entry.isDeleted && !baseById.get(entry.entryId)?.isDeleted;
    const deletedEntryIds = changed
      .filter(wasJustDeleted)
      .map((e) => e.entryId);
    const updated = changed.filter((entry) => !wasJustDeleted(entry));
    const nameChanged = name !== (baseName?.trim() ?? '');
    const isCreate = !editing.lexiconId;
    return {
      isCreate,
      name,
      nameChanged,
      added,
      updated,
      deletedEntryIds,
      purgedEntryIds,
      isDirty:
        isCreate ||
        nameChanged ||
        added.length > 0 ||
        updated.length > 0 ||
        deletedEntryIds.length > 0 ||
        purgedEntryIds.length > 0,
    };
  }
);

/** How many more entries the lexicon being edited can take (BR-16). */
/**
 * Room left under BR-16, counting TOMBSTONES.
 *
 * Deliberately not `liveEntries`. The ceiling this mirrors is the published
 * schema's `maxItems: 5000`, which counts every item in the array — a
 * soft-deleted entry included. Counting only live entries would tell an author
 * they had room right up until the platform refused the write with an opaque
 * rejection, which is the exact drift `limits.ts` exists to prevent. Purge is
 * how the room comes back.
 */
export const selectEditingHeadroom = createSelector(selectEditing, (editing) =>
  Math.max(0, LEXICON_MAX_ENTRIES - (editing?.draft.entries.length ?? 0))
);

export const selectBlockingCsvErrorCount = createSelector(
  selectCsvImport,
  (csvImport) => {
    const result = csvImport?.result;
    if (!result || result.ok) {
      return 0;
    }
    return result.cellErrors.filter((e) => e.blocking).length;
  }
);

/**
 * Whether the CSV import can be completed (U5-FR12).
 *
 * A file-level rejection blocks too, not just cell errors — a file that was
 * never parsed has no cell errors to count.
 */
export const selectCanCompleteCsvImport = createSelector(
  selectCsvImport,
  (csvImport) => {
    const result = csvImport?.result;
    if (!result) {
      return false;
    }
    return result.ok;
  }
);
